import {
  type RowActionsProps,
  type RowClickActionProps,
  type SearchAndFiltersProps,
  type StyleConfigProps,
  type UnifiedBulkActionsProps,
  type UnifiedRowActionsProps,
  type VisibilityElementProps,
  type WrappedElementProps,
} from '@blaze-runtime/engine-domain';
import { type ColumnId } from '@blaze-shared/api-contracts-domain';
import {
  type ActionsDisplayMode,
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithDirtyFieldProps,
  type CanvasElementWithFeaturesConfigProps,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type FieldSortConfig,
  type ListScrollMode,
  type PropertiesConfigImagePropertiesModel,
  type PropertiesConfigInputAlignModel,
  type PropertiesConfigInputBackground,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputCustomTemplateModel,
  type PropertiesConfigInputListColumnModel,
  type PropertiesConfigInputListCustomFieldModel,
  type PropertiesConfigInputListFieldModel,
  type PropertiesConfigInputListLayoutModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type TableConditionalFormatExpression,
  type TableConditionalFormatModel,
  type UnifiedActionKind,
} from '@blaze-shared/elements-domain';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { type ImageEmptyThumbnail } from '../image/image.models';

export type ListFlavor = 'list' | 'notification' | 'subscription-plan' | 'e-commerce' | 'custom';

export type ListImagePropertiesModel = PropertiesConfigImagePropertiesModel & {
  emptyThumbnail?: ImageEmptyThumbnail;
};

export interface PropertiesConfigInputListConditionalFormatModel {
  id: string;
  name: string;
  fill: PropertiesConfigInputColorModel;
  outline: PropertiesConfigInputColorModel;
  outlineWidth: ValueWithUnitOfMeasure;
  headline: PropertiesConfigInputColorModel;
  headlineWeight: string;
  description: PropertiesConfigInputColorModel;
  conditions: TableConditionalFormatModel;
  conditionsExpression?: TableConditionalFormatExpression[];
}

export interface SortValues {
  field: ColumnId | string;
  direction: 'asc' | 'desc' | '';
}

export const sortMap: Record<SortValues['direction'], 'Ascending' | 'Descending' | ''> = {
  asc: 'Ascending',
  desc: 'Descending',
  '': '',
};

export const DEFAULT_ROWS_COUNT = 1;
export const DEFAULT_COLS_COUNT = 1;
export const ROWS_GAP_PX = 8;

export const EXAMPLE_ELEMENT: DatasetRow = {
  image: 'assets/images/list-placeholder.svg',
  imageVertical: 'assets/images/list-vertical-placeholder.svg',
  imageNotification: 'assets/images/notification-placeholder.svg',
  heading: 'Heading',
  description: 'Description',
  name: 'Username',
  message: 'main message',
  isUnread: false,
  timestamp: '2023-05-18 16:30',
  title: 'Plan',
  price: 'price',
  bulletList: 'feature 1\nfeature 2\nfeature 3',
  itemName: 'Product',
  itemDescription: 'Product details...',
  itemPrice: 100,
};

type PropsModel = CanvasElementPropertiesWithData &
  CanvasElementWithDirtyFieldProps &
  CanvasElementWithFeaturesConfigProps &
  RowActionsProps &
  RowClickActionProps &
  SearchAndFiltersProps &
  StyleConfigProps &
  UnifiedBulkActionsProps &
  UnifiedRowActionsProps &
  VisibilityElementProps &
  WrappedElementProps;

export type ListPropsModel = PropsModel & {
  content: {
    actions: {
      align: string;
      disableActionWhenPending: boolean;
      items: PropertiesConfigInputContainerItemActionModel[];
      mode: ActionsDisplayMode;
    };
    columns: PropertiesConfigInputListColumnModel[];
    fields: PropertiesConfigInputListFieldModel;
    customFields: PropertiesConfigInputListCustomFieldModel[];
    list: {
      actionsView: UnifiedActionKind;
      bulkActionsDisplayMode: ActionsDisplayMode;
      layout: PropertiesConfigInputListLayoutModel;
      customTemplate?: PropertiesConfigInputCustomTemplateModel;
    };
    subscriptionPlan: {
      bulletIcon?: string;
    };
    sort: FieldSortConfig | FieldSortConfig[];
    title: ElementLabelProps;
    noDataMessage: string;
    mobile: {
      scrollMode: ListScrollMode;
    };
    conditionalFormatting: {
      enabled: boolean;
      formats: PropertiesConfigInputListConditionalFormatModel[];
    };
    indicator: {
      enabled: boolean;
      icon?: string;
      align: PropertiesConfigInputAlignModel;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      heading: PropertiesConfigInputColorModel;
      description: PropertiesConfigInputColorModel;
      actions: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
    };
    titleBlock: ElementTitleStyleProps;
    container: {
      fill?: PropertiesConfigInputColorModel;
      radius?: BorderRadiusValue;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
    };
    background: PropertiesConfigInputBackground;
    list: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      selectedRow: PropertiesConfigInputColorModel;
      hover: PropertiesConfigInputColorModel;
    };
    image: ListImagePropertiesModel;
    indicator: {
      color: PropertiesConfigInputColorModel;
      size: ValueWithUnitOfMeasure;
    };
  };
};
